/**
 * The Classy Kitchen — Collection Template
 * assets/tan-collection.js
 *
 * Vanilla JS, no dependencies. Progressively enhances a fully server-rendered
 * collection page: works with JS disabled (native links/forms), and upgrades
 * to AJAX filtering/sorting + load-more + quick add when JS is available.
 *
 * FIX: the AJAX facet-filtering code used to be wrapped in its own nested
 * IIFE, which meant applySort()/fetchFacetedResults() etc. were invisible to
 * the mobile Sort Drawer click handler defined earlier in the file. The
 * nested IIFE has been unwrapped so every helper shares one scope.
 *
 * FIX2: tag filters (color/size/...) live at Shopify's URL PATH level
 * (/collections/handle/color_red), while native filters (price,
 * availability) live in the QUERY STRING. `currentCollectionPath` tracks
 * whichever tag-inclusive path is actually in effect so AJAX requests never
 * silently drop an active tag when a native filter or sort changes.
 *
 * FIX3: replaced the old centered variant-picker modal with a Swiggy/
 * Zomato-style in-place stepper on the card.
 *
 * FIX4: Amazon-style stepper — left button shows trash at qty 1, "−" at 2+.
 *
 * FIX5: no color/option picker — every Add tap resolves the first available
 * variant automatically and adds directly.
 *
 * FIX6/FIX8: real cart drawer opens instead of the custom #TanMiniCart
 * panel, which was removed entirely.
 *
 * FIX9 → FIX16 (superseded by FIX17 below): several passes tried to work
 * around what looked like a third-party checkout app ("Shopflo") keeping
 * its own separate cart-drawer state, based on window.Shopify.actions
 * .openCart() and various guessed Shopflo APIs. A reference version of
 * this same template (ck-collection.js) turned up that never had this
 * problem at all — it opens the cart with the theme's own native
 * <cart-drawer>/<cart-notification> element's own .open() method, and
 * relies on that element's own built-in 'cart:updated' listener to
 * re-render itself, instead of manually calling renderContents() and
 * separately trying to trigger a third-party drawer. That's simpler, uses
 * only confirmed, standard theme APIs, and needs none of the
 * Shopflo-specific guessing the last several passes were built on — see
 * FIX17.
 *
 * FIX17 (this pass): rebuilt cart-reveal and Buy Now on the reference's
 * simpler pattern.
 *   - openCartUI(cart, reveal): dispatches 'cart:updated' FIRST (the
 *     native <cart-drawer>/<cart-notification> listens for this itself
 *     and re-renders its own contents synchronously, before
 *     dispatchEvent() even returns — so by the time reveal happens, the
 *     element's content is already correct, no waiting/nudging needed).
 *     Then, only if reveal is true, calls the element's own .open()
 *     method. Falls back to window.Shopify.actions.openCart() and then a
 *     plain cart-icon click only if no such element/method exists.
 *   - All the FIX14/FIX15/FIX16 machinery (whenCartDrawerReady,
 *     tryShopfloRefreshHook, revealCartDrawer's warm-up delay,
 *     goToCheckout/goToCheckoutDirect/openShopfloCheckoutAction/
 *     clickCheckoutButton) is removed — none of it is needed once the
 *     drawer is opened through its own real .open() method.
 *   - Buy Now: adds to cart silently, then hands off to
 *     goToShopfloCheckout() — see FIX18 below.
 *
 * FIX18 (this pass): a plain CHECKOUT_URL navigation for Buy Now fires NO
 * click event, so a checkout-accelerator app like Shopflo — which works
 * by intercepting a CLICK on Shopify's native checkout button — never
 * gets a chance to engage, and the shopper lands on Shopify's bare
 * default checkout instead of Shopflo's UI. goToShopfloCheckout() now
 * opens the drawer (safe to do since FIX17 fixed the empty-cart bug that
 * made FIX15/16 avoid opening it) and clicks the real checkout button
 * inside it, falling back to a plain navigation only if no such button
 * ever appears.
 *
 * FIX19 (this pass): a screen recording showed Buy Now → checkout working
 * perfectly end to end (correct product/price through shipping + payment)
 * and confirmed this store's checkout has no Shopflo branding at all — so
 * FIX18's Shopflo-specific click-hunt was solving a problem that isn't
 * actually present here; it's kept as-is since it's harmless and the
 * fallback (plain navigation) is what's really doing the work. The SAME
 * recording caught a real bug: the side drawer stuck on "Your cart is
 * empty" for the entire session across four separate Adds, never
 * self-correcting, while the product cards behind it (driven by the real
 * /cart.js data) were always correct. Cause: openCartUI() was ALSO
 * calling drawer.renderContents(cart, false) as a "defensive extra" —
 * Shopify's real cart-drawer component expects that method to receive a
 * `sections`-shaped AJAX response, not a raw /cart.js object, so the
 * wrong-shaped call was stomping on the correct render the native
 * 'cart:updated' listener had just done. That call is removed; openCartUI
 * now does exactly what the reference (ck-collection.js) does — dispatch
 * the event, then just .open().
 */
(function () {
  'use strict';

  var root = document.getElementById('MainCollection');
  if (!root) return;

  var collectionUrl = root.dataset.collectionUrl; // base collection path — Shopify never includes tag segments here
  var sectionId = root.dataset.sectionId;
  var currentCollectionPath = window.location.pathname; // FIX2: tag-inclusive path currently in effect
  var debounceTimer = null;

  var shopifyRoutes = (window.Shopify && window.Shopify.routes) || {};
  var CART_ADD_URL = shopifyRoutes.cart_add_url || '/cart/add.js';
  var CART_CHANGE_URL = shopifyRoutes.cart_change_url || '/cart/change.js';
  var CART_URL = shopifyRoutes.cart_url || '/cart.js';
  var CART_PAGE_URL = (shopifyRoutes.root ? shopifyRoutes.root + 'cart' : '/cart');
  var CHECKOUT_URL = (shopifyRoutes.root ? shopifyRoutes.root + 'checkout' : '/checkout');

  /* ------------------------------------------------------------------ */
  /* Utilities                                                          */
  /* ------------------------------------------------------------------ */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function debounce(fn, wait) {
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () { fn.apply(context, args); }, wait);
    };
  }

  function trapFocus(container) {
    var focusable = qsa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])', container);
    if (!focusable.length) return function () {};
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handler);
    first.focus();
    return function () { container.removeEventListener('keydown', handler); };
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    return '₹' + (cents / 100).toFixed(2);
  }

  function productImageThumb(url) {
    if (!url) return '';
    return url.replace(/(\.[a-zA-Z]{3,4})(\?.*)?$/, '_120x120$1$2');
  }

  /* ---------------------------------------------------------
     FIX19: the theme's native <cart-drawer>/<cart-notification>
     listens for 'cart:updated' itself and re-renders correctly
     from it (confirmed working, matches ck-collection.js). This
     used to ALSO call drawer.renderContents(cart, false) here as
     a "defensive extra" — but a screen recording showed the
     drawer stuck on "Your cart is empty" for the whole session,
     never correcting, while the product cards behind it (driven
     by the real /cart.js data) were always right. Shopify's real
     cart-drawer component expects renderContents() to receive a
     `sections`-shaped AJAX response, not a raw /cart.js cart
     object — passing the wrong shape stomps on the correct
     render the 'cart:updated' listener had just done. Removed
     entirely; this now does exactly what the reference does:
     dispatch the event (in notifyCartUpdated) and just open().
     --------------------------------------------------------- */
  function openCartUI(cart, reveal) {
    if (!reveal) return;

    var drawer = qs('cart-drawer, cart-notification');

    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    try {
      if (window.Shopify && window.Shopify.actions && typeof window.Shopify.actions.openCart === 'function') {
        window.Shopify.actions.openCart();
        return;
      }
    } catch (e) {
      // fall through to the fallback below
    }

    var candidateSelectors = [
      '[data-cart-icon]',
      '[data-cart-drawer-toggle]',
      '#cart-icon-bubble',
      '.header__icon--cart',
      '.cart-icon',
      'a[href="/cart"]',
      'button[aria-controls*="cart" i]'
    ];
    for (var i = 0; i < candidateSelectors.length; i++) {
      var el = qs(candidateSelectors[i]);
      if (el) {
        el.click();
        return;
      }
    }
  }

  /* ---------------------------------------------------------
     FIX18 — Shopflo checkout for Buy Now. A raw
     window.location.href navigation fires NO click event at
     all, so a checkout-accelerator app like Shopflo (which
     works by intercepting a click on Shopify's native checkout
     button) never gets a chance to engage — the shopper would
     just land on Shopify's bare default checkout instead of
     Shopflo's UI. So this actually clicks a real checkout
     trigger instead of just navigating.

     Safe to open the drawer for this now (FIX17 fixed the
     empty-cart bug that made FIX15/16 avoid opening it): open
     it with the freshly-added item, poll briefly for a real,
     visible checkout button inside it, and click that — giving
     Shopflo (or whatever else is wired to that click) its
     chance to intercept. Falls back to a plain CHECKOUT_URL
     navigation only if no checkout trigger ever appears.
     --------------------------------------------------------- */
  function clickCheckoutButton() {
    var candidateSelectors = [
      'form[action^="/cart"] button[name="checkout"]',
      'button[name="checkout"]',
      '#CartDrawer-Checkout',
      '.cart__checkout-button',
      '[data-shopflo-checkout]',
      'a[href="/checkout"]'
    ];
    for (var i = 0; i < candidateSelectors.length; i++) {
      var el = qs(candidateSelectors[i]);
      if (el && el.offsetParent !== null) {
        el.click();
        return true;
      }
    }
    var all = qsa('button, a');
    for (var j = 0; j < all.length; j++) {
      var text = (all[j].textContent || '').trim().toLowerCase();
      if (text === 'checkout' && all[j].offsetParent !== null) {
        all[j].click();
        return true;
      }
    }
    return false;
  }

  function goToShopfloCheckout(cart, onSettled) {
    openCartUI(cart, true);

    var maxWaitMs = 3000;
    var intervalMs = 120;
    var waited = 0;

    var poll = setInterval(function () {
      var clicked = clickCheckoutButton();
      waited += intervalMs;
      if (clicked || waited >= maxWaitMs) {
        clearInterval(poll);
        if (!clicked) {
          window.location.href = CHECKOUT_URL;
        }
        if (typeof onSettled === 'function') onSettled();
      }
    }, intervalMs);
  }

  /* ------------------------------------------------------------------ */
  /* Drawers (filter + sort) — unchanged                                */
  /* ------------------------------------------------------------------ */
  var releaseFocusTrap = null;
  var lastFocusedEl = null;

  function openDrawer(drawer, triggerBtn) {
    if (!drawer) return;
    lastFocusedEl = triggerBtn || document.activeElement;
    drawer.hidden = false;
    requestAnimationFrame(function () { drawer.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
    releaseFocusTrap = trapFocus(drawer.querySelector('.tan-drawer__panel') || drawer);
    if (triggerBtn) triggerBtn.setAttribute('aria-expanded', 'true');
  }

  function closeDrawer(drawer) {
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    if (releaseFocusTrap) { releaseFocusTrap(); releaseFocusTrap = null; }
    setTimeout(function () { drawer.hidden = true; }, 280);
    if (lastFocusedEl) lastFocusedEl.focus();
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('.tan-filter-open');
    if (opener) { openDrawer(qs('#FilterDrawer'), opener); return; }

    var sortOpener = e.target.closest('#MobileSortTrigger');
    if (sortOpener) { openDrawer(qs('#SortDrawer'), sortOpener); return; }

    var closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      var drawer = closer.closest('.tan-drawer');
      closeDrawer(drawer);
      return;
    }

    var sortOption = e.target.closest('.tan-sort-option');
    if (sortOption) {
      applySort(sortOption.dataset.sortValue);
      closeDrawer(qs('#SortDrawer'));
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    qsa('.tan-drawer.is-open').forEach(closeDrawer);
  });

  /* ------------------------------------------------------------------ */
  /* AJAX facet filtering + sorting — unchanged                         */
  /* ------------------------------------------------------------------ */

  function getCurrentFacetForm() {
    var drawerForm = qs('#FilterDrawer [data-facet-form]');
    var sidebarForm = qs('.tan-sidebar [data-facet-form]');
    if (drawerForm && isVisible(drawerForm)) return drawerForm;
    if (sidebarForm && isVisible(sidebarForm)) return sidebarForm;
    return drawerForm || sidebarForm || qs('[data-facet-form]');
  }

  function isVisible(element) {
    if (!element) return false;
    var style = window.getComputedStyle(element);
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      element.offsetWidth > 0 &&
      element.offsetHeight > 0
    );
  }

  function currentParams() {
    var params = new URLSearchParams(window.location.search);
    var form = getCurrentFacetForm();

    Array.from(params.keys()).forEach(function (key) {
      if (key.indexOf('filter.') === 0 || key === 'page') {
        params.delete(key);
      }
    });

    if (form) {
      var formData = new FormData(form);
      formData.forEach(function (value, key) {
        if (value === '') return;
        if (key.indexOf('filter.') === 0) {
          params.append(key, value);
        }
      });
    }

    return params;
  }

  function getActiveTagCount(path) {
    if (!collectionUrl) return 0;
    var base = collectionUrl.replace(/\/$/, '');
    var current = (path || currentCollectionPath).replace(/\/$/, '');
    if (current === base) return 0;
    if (current.indexOf(base + '/') !== 0) return 0;
    var remainder = current.slice(base.length + 1);
    if (!remainder) return 0;
    return remainder.split('+').filter(Boolean).length;
  }

  function fetchFacetedResults(params, pushHistory, path) {
    if (!params) params = new URLSearchParams(window.location.search);
    if (pushHistory === undefined) pushHistory = true;
    if (!path) path = currentCollectionPath;

    root.classList.add('is-loading');
    root.setAttribute('aria-busy', 'true');

    var url = path + '?section_id=' + encodeURIComponent(sectionId);
    var query = params.toString();
    if (query) url += '&' + query;

    fetch(url, {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Collection request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        replaceRegion(doc, '#CollectionGrid');
        replaceRegion(doc, '#FilterDrawerContent');
        replaceRegion(doc, '#FilterSidebar');
        replaceRegion(doc, '#ActiveFilters');
        replaceRegion(doc, '#ProductCount');
        replaceRegion(doc, '#CollectionCount');

        currentCollectionPath = path;

        syncSortValue(params);
        updateActiveFilterCount(params);

        if (pushHistory) {
          var newUrl = path + (params.toString() ? '?' + params.toString() : '');
          window.history.pushState({ collectionFilter: true }, '', newUrl);
        }

        root.classList.remove('is-loading');
        root.removeAttribute('aria-busy');

        var grid = qs('#CollectionGrid');
        if (grid) grid.setAttribute('tabindex', '-1');

        observeCards();
        refreshCartUiFromServer(false); // re-apply stepper state to the freshly-swapped-in cards, don't force-open the panel

        if (typeof window.initCollection === 'function') {
          window.initCollection();
        }
      })
      .catch(function (error) {
        console.error('Classy Kitchen filter error:', error);
        root.classList.remove('is-loading');
        root.removeAttribute('aria-busy');
      });
  }

  function replaceRegion(doc, selector) {
    var incoming = doc.querySelector(selector);
    var current = document.querySelector(selector);
    if (!incoming || !current) return;
    current.replaceWith(incoming.cloneNode(true));
  }

  function updateActiveFilterCount(params) {
    var count = 0;
    params.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0 && value !== '') count++;
    });
    count += getActiveTagCount();

    qsa('#ActiveFilterCount').forEach(function (el) {
      if (count > 0) {
        el.hidden = false;
        el.textContent = count;
      } else {
        el.hidden = true;
        el.textContent = '';
      }
    });
  }

  function applySort(value) {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    if (value) params.set('sort_by', value);
    else params.delete('sort_by');
    fetchFacetedResults(params, true);
  }

  function syncSortValue(params) {
    var sortValue = params.get('sort_by');
    qsa('#SortBy').forEach(function (select) {
      if (sortValue) select.value = sortValue;
    });
  }

  document.addEventListener('change', function (event) {
    var target = event.target;

    if (target && target.matches('[data-facet-input]')) {
      debounce(function () {
        var params = currentParams();
        params.delete('page');
        fetchFacetedResults(params, true);
      }, 250)();
      return;
    }

    if (target && target.matches('#SortBy')) {
      applySort(target.value);
    }
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form && form.matches('[data-facet-form]'))) return;

    event.preventDefault();

    var params = new URLSearchParams(window.location.search);
    Array.from(params.keys()).forEach(function (key) {
      if (key.indexOf('filter.') === 0 || key === 'page') params.delete(key);
    });

    var formData = new FormData(form);
    formData.forEach(function (value, key) {
      if (value !== '' && key.indexOf('filter.') === 0) params.append(key, value);
    });

    fetchFacetedResults(params, true);
  });

  document.addEventListener('click', function (event) {
    var link = event.target.closest('[data-ajax-filter-link]');
    if (!link) return;

    event.preventDefault();

    var url;
    try {
      url = new URL(link.href, window.location.origin);
    } catch (error) {
      console.error('Invalid filter URL:', error);
      return;
    }

    var params = url.searchParams;
    params.delete('page');
    fetchFacetedResults(params, true, url.pathname);
  });

  document.addEventListener('click', function (event) {
    var clearButton = event.target.closest('[data-clear-filters]');
    if (!clearButton) return;

    event.preventDefault();

    var params = new URLSearchParams(window.location.search);
    Array.from(params.keys()).forEach(function (key) {
      if (key.indexOf('filter.') === 0 || key === 'page') params.delete(key);
    });

    fetchFacetedResults(params, true, collectionUrl);
  });

  window.addEventListener('popstate', function () {
    var params = new URLSearchParams(window.location.search);
    fetchFacetedResults(params, false, window.location.pathname);
  });

  /* ------------------------------------------------------------------ */
  /* Load more pagination — unchanged, now also re-syncs cart state     */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.tan-load-more');
    if (!btn) return;
    btn.dataset.loading = 'true';
    btn.textContent = 'Loading…';

    fetch(btn.dataset.nextUrl + (btn.dataset.nextUrl.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId))
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newItems = qsa('.tan-grid__item', doc);
        var grid = qs('.tan-grid');
        newItems.forEach(function (item) { grid.appendChild(item); });

        var newPagination = doc.querySelector('#Pagination');
        var currentPagination = qs('#Pagination');
        if (newPagination && currentPagination) currentPagination.replaceWith(newPagination);
        else if (currentPagination) currentPagination.remove();

        observeCards();
        refreshCartUiFromServer(false);
      })
      .catch(function () {
        btn.dataset.loading = 'false';
        btn.textContent = 'Load more';
      });
  });

  /* ------------------------------------------------------------------ */
  /* Entrance animation on scroll — unchanged                           */
  /* ------------------------------------------------------------------ */
  var io;
  var animationsEnabled = root.dataset.animations === 'true';
  function observeCards() {
    var items = qsa('.tan-grid__item');

    if (!animationsEnabled || !('IntersectionObserver' in window)) {
      items.forEach(function (item) { item.classList.remove('tan-anim-init'); });
      return;
    }

    if (io) io.disconnect();
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '80px' });

    items.forEach(function (item, i) {
      item.classList.add('tan-anim-init');
      item.classList.remove('is-visible');
      item.style.transitionDelay = Math.min(i * 30, 240) + 'ms';
      io.observe(item);
    });

    setTimeout(function () {
      qsa('.tan-grid__item.tan-anim-init:not(.is-visible)').forEach(function (item) {
        item.classList.add('is-visible');
      });
    }, 1200);
  }

  /* ------------------------------------------------------------------ */
  /* Wishlist — unchanged                                                */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    var pressed = btn.getAttribute('aria-pressed') === 'true';
    btn.setAttribute('aria-pressed', String(!pressed));
    document.dispatchEvent(new CustomEvent('wishlist:toggle', {
      detail: { productId: btn.dataset.productId, added: !pressed }
    }));
  });

  /* ==================================================================== */
  /* CART — shared low-level calls                                        */
  /* ==================================================================== */

  function getCart() {
    return fetch(CART_URL, { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function cartAdd(variantId, quantity, reveal) {
    if (reveal === undefined) reveal = true;
    return fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw err; });
        return res.json();
      })
      .then(function () { return getCart(); })
      .then(function (cart) { notifyCartUpdated(cart, reveal); return cart; });
  }

  function cartChangeQuantity(variantId, quantity) {
    return fetch(CART_CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: Math.max(quantity, 0) })
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) { notifyCartUpdated(cart, false); return cart; });
  }

  /* ---------------------------------------------------------
     NOTIFY CART UPDATED — single point every cart mutation
     (card Add, card stepper, Buy Now) funnels through.
     FIX17: dispatches 'cart:updated' FIRST — the theme's native
     <cart-drawer>/<cart-notification> listens for this itself
     and re-renders synchronously before dispatchEvent() returns
     — then openCartUI() only has to open it, never race it.
     --------------------------------------------------------- */
  function notifyCartUpdated(cart, reveal) {
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart, openMiniCart: false } }));

    updateCartCountBadges(cart.item_count);
    syncAddControls(cart);

    openCartUI(cart, reveal);
  }


  function updateCartCountBadges(count) {
    qsa('[data-cart-count], .cart-count, #cart-icon-bubble .cart-count').forEach(function (el) {
      el.textContent = count;
      if ('hidden' in el) el.hidden = count === 0;
    });
  }

  /* ==================================================================== */
  /* CARD ADD BUTTON ↔ STEPPER (Amazon/Swiggy/Zomato style)                */
  /* ==================================================================== */

  function readMeta(el) {
    return {
      productHandle: el.dataset.productHandle || '',
      defaultLabel: el.dataset.defaultLabel || 'Add',
      requiresSelection: el.dataset.requiresSelection || 'false',
      productTitle: el.dataset.productTitle || ''
    };
  }

  function updateMinusIcon(btn, qty) {
    if (qty <= 1) {
      btn.innerHTML = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 4h10M6.5 4V2.5h3V4M4.5 4l.6 9a1 1 0 001 .9h3.8a1 1 0 001-.9l.6-9" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      btn.setAttribute('aria-label', 'Remove from cart');
    } else {
      btn.textContent = '−';
      btn.setAttribute('aria-label', 'Decrease quantity');
    }
  }

  function buildStepper(oldEl, variantId, quantity, meta) {
    var stepper = document.createElement('div');
    stepper.className = 'tan-stepper';
    stepper.dataset.variantId = variantId;
    stepper.dataset.productHandle = meta.productHandle || '';
    stepper.dataset.defaultLabel = meta.defaultLabel || 'Add';
    stepper.dataset.requiresSelection = meta.requiresSelection || 'false';
    stepper.dataset.productTitle = meta.productTitle || '';
    stepper.dataset.btnClass = oldEl.dataset && oldEl.dataset.btnClass
      ? oldEl.dataset.btnClass
      : (oldEl.className || 'tan-add-btn');

    var minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'tan-stepper__btn tan-stepper__btn--minus';

    var qtyEl = document.createElement('span');
    qtyEl.className = 'tan-stepper__qty';
    qtyEl.textContent = quantity;

    var plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'tan-stepper__btn tan-stepper__btn--plus';
    plus.setAttribute('aria-label', 'Increase quantity');
    plus.textContent = '+';

    updateMinusIcon(minus, quantity);

    stepper.appendChild(minus);
    stepper.appendChild(qtyEl);
    stepper.appendChild(plus);

    var pending = false;
    function commit(nextQty) {
      if (pending) return;
      pending = true;
      stepper.classList.add('is-busy');
      cartChangeQuantity(variantId, nextQty).then(cleanup, cleanup);
      function cleanup() {
        pending = false;
        stepper.classList.remove('is-busy');
      }
    }

    minus.addEventListener('click', function () { commit(parseInt(qtyEl.textContent, 10) - 1); });
    plus.addEventListener('click', function () { commit(parseInt(qtyEl.textContent, 10) + 1); });

    oldEl.replaceWith(stepper);
    return stepper;
  }

  function revertToAddButton(stepper) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = stepper.dataset.btnClass || 'tan-add-btn';
    btn.setAttribute('data-quitan-add', '');
    btn.dataset.btnClass = stepper.dataset.btnClass || 'tan-add-btn';
    btn.dataset.productHandle = stepper.dataset.productHandle || '';
    btn.dataset.defaultLabel = stepper.dataset.defaultLabel || 'Add';
    btn.dataset.requiresSelection = stepper.dataset.requiresSelection || 'false';
    btn.dataset.productTitle = stepper.dataset.productTitle || '';
    if (stepper.dataset.requiresSelection !== 'true') {
      btn.dataset.variantId = stepper.dataset.variantId;
    }
    btn.textContent = stepper.dataset.defaultLabel || 'Add';
    stepper.replaceWith(btn);
    return btn;
  }

  function syncAddControls(cart) {
    var qtyByVariant = {};
    cart.items.forEach(function (item) { qtyByVariant[item.variant_id] = item.quantity; });

    qsa('[data-quitan-add]').forEach(function (btn) {
      if (btn.dataset.requiresSelection === 'true') return;
      var vid = Number(btn.dataset.variantId);
      if (vid && qtyByVariant[vid]) {
        buildStepper(btn, vid, qtyByVariant[vid], readMeta(btn));
      }
    });

    qsa('.tan-stepper[data-variant-id]').forEach(function (stepper) {
      var vid = Number(stepper.dataset.variantId);
      var qty = qtyByVariant[vid] || 0;
      if (qty <= 0) {
        revertToAddButton(stepper);
        return;
      }
      var qtyEl = stepper.querySelector('.tan-stepper__qty');
      var minusBtn = stepper.querySelector('.tan-stepper__btn--minus');
      if (qtyEl) qtyEl.textContent = qty;
      if (minusBtn) updateMinusIcon(minusBtn, qty);
    });
  }

  function resolveVariantId(btn) {
    if (btn.dataset.variantId) return { id: btn.dataset.variantId, title: btn.dataset.productTitle };

    var script = document.querySelector('[data-quitan-add-json="' + btn.dataset.productHandle + '"]');
    if (!script) return null;
    var data = JSON.parse(script.textContent);
    var variant = (data.variants || []).find(function (v) { return v.available; }) || (data.variants || [])[0];
    if (!variant) return null;
    return { id: variant.id, title: data.title };
  }

  function addSimpleFromButton(btn) {
    var resolved = resolveVariantId(btn);
    if (!resolved) return;

    btn.disabled = true;
    btn.classList.add('is-busy');

    var meta = {
      productHandle: btn.dataset.productHandle,
      defaultLabel: btn.dataset.defaultLabel,
      requiresSelection: 'false',
      productTitle: resolved.title || btn.dataset.productTitle
    };

    cartAdd(resolved.id, 1)
      .then(function () {
        buildStepper(btn, resolved.id, 1, meta);
      })
      .catch(function () {
        btn.disabled = false;
        btn.classList.remove('is-busy');
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quitan-add]');
    if (!btn || btn.disabled) return;
    addSimpleFromButton(btn);
  });

  /* ---------------------------------------------------------
     BUY NOW (FIX18) — adds to cart silently, then hands off to
     goToShopfloCheckout() to open the drawer and click the real
     checkout button (see FIX18 note above for why a plain
     navigation alone isn't enough).
     --------------------------------------------------------- */
  function addBuyNowFromButton(btn) {
    var resolved = resolveVariantId(btn);
    if (!resolved) return;

    var originalLabel = btn.dataset.defaultLabel || btn.textContent;
    var pairAddBtn = qs('[data-quitan-add][data-product-handle="' + btn.dataset.productHandle + '"]');

    btn.disabled = true;
    btn.classList.add('is-busy');
    btn.textContent = 'Processing…';
    if (pairAddBtn) pairAddBtn.disabled = true;

    function release() {
      btn.disabled = false;
      btn.classList.remove('is-busy');
      btn.textContent = originalLabel;
      if (pairAddBtn) pairAddBtn.disabled = false;
    }

    cartAdd(resolved.id, 1, false)
      .then(function (cart) {
        goToShopfloCheckout(cart, release);
      })
      .catch(function () {
        release();
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quitan-buynow]');
    if (!btn || btn.disabled) return;
    addBuyNowFromButton(btn);
  });

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    qsa('[data-quitan-buynow]').forEach(function (btn) {
      btn.disabled = false;
      btn.classList.remove('is-busy');
      btn.textContent = btn.dataset.defaultLabel || 'Buy Now';
    });
    qsa('[data-quitan-add]').forEach(function (btn) {
      btn.disabled = false;
    });
  });

  /* ------------------------------------------------------------------ */
  /* Init / re-sync entry point                                         */
  /* ------------------------------------------------------------------ */
  function refreshCartUiFromServer(reveal) {
    getCart().then(function (cart) {
      syncAddControls(cart);
    }).catch(function () {});
  }

  observeCards();
  refreshCartUiFromServer(false);
})();
